import { useEffect, useState } from "react";
import { useAuthContext } from "./useAuthContext";
import axios from 'axios';

export const useMyTimesheets = () => {

    const { username, user, clientApi, dispatch: authDispatch } = useAuthContext();
    const [isLoading, setIsLoading] = useState(true);

    const axiosInstance = axios.create({
        baseURL: `${clientApi}`,
        headers: {
            accepts: "application/json",
            "Content-Type": "application/json",
        },
    });

    axiosInstance.interceptors.request.use(request => {
        const accessToken = user.token;
        if (accessToken) {
            request.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return request;
    }, error => {
        return Promise.reject(error);
    });

    axiosInstance.interceptors.response.use(
        response => response,
        async error => {
            console.log('Refresh Access Token Expired!!!')
            const originalRequest = error.config;
            if (error.response.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true; // Mark the request as retried to avoid infinite loops.
                try {
                    // Make a request to your auth server to refresh the token.
                    const response = await axios.post(`${clientApi}api/auth/refresh`, user);
                    // console.log(response.data)
                    // Store the new access and refresh tokens.
                    authDispatch({ type: 'Refresh', payload: response.data, clientApi: clientApi, username: username });

                    // Update the authorization header with the new access token.
                    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                    return axios(originalRequest); // Retry the original request with the new access token.
                } catch (refreshError) {
                    // Handle refresh token errors by clearing stored tokens and redirecting to the login page.
                    console.error('Token refresh failed:', refreshError);
                    authDispatch({ type: 'LOGOUT' });
                    return Promise.reject(refreshError);
                }
            }
            return Promise.reject(error); // For all other errors, return the error as is.
        }
    );

    //#region Save/Submit/Delete
    const saveTimesheetLines = async (
        lines
    ) => {
        try {
            setIsLoading(true);

            const url = `${clientApi}api/timesheets/SaveTimesheetLines`;
            const headers = { Authorization: `Bearer ${user.token}`, Username: username };
            const payload = lines;
            const response = await axiosInstance.post(url, payload, { headers });

            setIsLoading(false);
            return response.data;
        } catch (error) {
            setIsLoading(false);
            console.error('Error saving timesheet lines:', error);
            throw error;
        }
    };

    const saveTimesheetInfo = async (
        tsInfo
    ) => {
        try {
            setIsLoading(true);
            console.log(tsInfo)
            const url = `${clientApi}api/timesheets/SaveTimesheetInfo`;
            const headers = {Authorization: `Bearer ${user.token}`, Username: username };
            const response = await axiosInstance.post(url, tsInfo, { headers });

            setIsLoading(false);
            return response.data;
        } catch (error) {
            setIsLoading(false);
            console.error('Error saving timesheet info:', error);
            throw error;
        }
    };

    const submitTimesheet = async (
        tsnoUI
    ) => {
        try {
            setIsLoading(true);
            var fetchCmd = `${clientApi}api/timesheets/SubmitTimesheet`;
            const response = await axiosInstance.get(fetchCmd, {
                params: { username: username, tsnoUI: tsnoUI },
            })
            setIsLoading(false);
            return response.data;
        } catch (error) {
            setIsLoading(false);
            console.error('Error saving timesheet info:', error);
            throw error;
        }
    };

    const deleteTimesheetLines = async (
        deletedUI
    ) => {
        try {
            setIsLoading(true);
            const url = `${clientApi}api/timesheets/DeleteTimesheetLines`;
            const headers = { Authorization: `Bearer ${user.token}`, Username: username };
            const response = await axiosInstance.post(url, deletedUI, { headers });

            setIsLoading(false);
            console.log(response.data.message)
            return response.data;
        } catch (error) {
            setIsLoading(false);
            console.error('Error saving timesheet lines:', error);
            throw error;
        }
    };
    //#endregion

    //#region Save/Delete Expense
    const saveExpenseLines = async (
        expenseLines
    ) => {
        try {
            setIsLoading(true);
            const url = `${clientApi}api/timesheets/SaveExpenseLines`;
            const headers = { Authorization: `Bearer ${user.token}`, Username: username };
            const response = await axiosInstance.post(url, expenseLines, { headers });
            setIsLoading(false);
            return response.data;
        } catch (error) {
            setIsLoading(false);
            console.error('Error saving expense lines:', error);
            throw error;
        }
    };

    const deleteExpenseLines = async ( deletedRowGuids) => {
        try {
            setIsLoading(true);
            const url = `${clientApi}api/timesheets/DeleteExpenseLines`;
            const headers = { Authorization: `Bearer ${user.token}`, Username: username };
            const response = await axiosInstance.post(url, deletedRowGuids, { headers });
            setIsLoading(false);
            return response.data;
        } catch (error) {
            setIsLoading(false);
            console.error('Error deleting expense lines:', error);
            throw error;
        }
    };
    //#endregion

    //#region Save/Delete Attachments
    const saveAttachments = async (
        attachments
    ) => {
        try {
            setIsLoading(true);
            const url = `${clientApi}api/timesheets/SaveExpenseAttachments`;
            const headers = { Authorization: `Bearer ${user.token}`, Username: username };
            const response = await axiosInstance.post(url, attachments, { headers });
            setIsLoading(false);
            return response.data;
        } catch (error) {
            setIsLoading(false);
            console.error('Error saving attachments:', error);
            throw error;
        }
    };

    const deleteAttachments = async (
        rowguids
    ) => {
        try {
            setIsLoading(true);
            const url = `${clientApi}api/timesheets/DeleteExpenseAttachments`;
            const headers = { Authorization: `Bearer ${user.token}`, Username: username };
            const response = await axiosInstance.post(url, rowguids, { headers });
            setIsLoading(false);
            return response.data;
        } catch (error) {
            console.error('Error deleting attachments:', error);
            throw error;
        }
    };
    //#endregion

    //#region Load Data
    const getPhotoByID = async ( employeeID
    ) => {
        try {
            setIsLoading(true);
            const response = await axiosInstance.get(`${clientApi}api/Employee/EmployeePhoto/${employeeID}`)
            setIsLoading(false)
            //console.log(response.data)
            return response.data;
        } catch (error) {
            //console.log(error)
            return '';
        } finally {
            setIsLoading(false)
        }
    };

    const getEType = async (
    ) => {
        try {
            setIsLoading(true);
            const response = await axiosInstance.get(`${clientApi}api/Employee/GetEmployementTypeByUsername`, {
                params: { username: username },
            })
            setIsLoading(false)
            //console.log(response.data)
            return response.data;
        } catch (error) {
            console.log(error)
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const getTSDates = async (
    ) => {
        try {
            setIsLoading(true)
            var fetchCmd = `${clientApi}api/timesheets/GetTimesheetDates`;
            const response = await axiosInstance.get(fetchCmd, {
                params: { username: username },
            })
            setIsLoading(false)
            //console.log(response.data)
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const loadUserTimesheets = async (
        employeeId
    ) => {
        try {
            setIsLoading(true)
            var fetchCmd = `${clientApi}api/timesheets/GetUserTimesheets`;
            const response = await axiosInstance.get(fetchCmd, {
                params: { username: username, userid: employeeId },
            })
            setIsLoading(false)
            //console.log(response.data)
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const loadNextTimesheet = async (
        obj
    ) => {
        try {
            setIsLoading(true)
            const payload = [{
                tsnoUI: obj[1],
                year: 0,
                month: 0,
                period: obj[0].toString(),
                isSubmitted: false,
                isApproved: false,
                inCash: false,
                inTimeOff: false
            }];
            var fetchCmd = `${clientApi}api/timesheets/GetNextTimesheet`;
            const response = await axiosInstance.post(fetchCmd, payload, {
                params: { username: username },
            })
            setIsLoading(false)
            //console.log(response.data)
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const loadNewTimesheet = async (
        obj
    ) => {
        try {
            setIsLoading(true)
            const payload = {
                tsnoUI: obj[0],
                year: obj[1],
                month: obj[2],
                period: obj[5] ? '' : obj[3],
                isSubmitted: false,
                isApproved: false,
                inCash: false,
                inTimeOff: false,
                weekStart: obj[5] ? obj[4].weekStart : '',
                weekEnd: obj[5] ? obj[4].weekEnd : '', 
            };

            var fetchCmd = `${clientApi}api/timesheets/GetNewTimesheet`;
            const response = await axiosInstance.post(fetchCmd, payload, {
                params: { username: username, employeeID: obj[6] ? obj[6].employeeId : '' },
            })
            setIsLoading(false)
            //console.log(response.data)
            return response.data;
        } catch (error) {
            console.log(error)
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const loadChargeableLines = async (
        tsnoUI
    ) => {
        try {
            setIsLoading(true)
            var fetchCmd = `${clientApi}api/timesheets/GetChargeableLines`;
            const response = await axiosInstance.get(fetchCmd, {
                params: { username: username, tsnoUI: tsnoUI },
            })
            setIsLoading(false)
            //console.log(response.data)
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const loadNonChargeableLines = async (
        tsnoUI
    ) => {
        try {
            setIsLoading(true)
            var fetchCmd = `${clientApi}api/timesheets/GetNonChargeableLines`;
            const response = await axiosInstance.get(fetchCmd, {
                params: { username: username, tsnoUI: tsnoUI },
            })
            setIsLoading(false)
            //console.log(response.data)
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const loadExpenseLines = async (
        tsnoUI
    ) => {
        try {
            setIsLoading(true)
            var fetchCmd = `${clientApi}api/timesheets/GetExpenseLines`;
            const response = await axiosInstance.get(fetchCmd, {
                params: { username: username, tsnoUI: tsnoUI },
            })
            setIsLoading(false)
            //console.log(response.data)
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const loadExpenseAttachments = async (
        tsnoUI
    ) => {
        try {
            setIsLoading(true)
            var fetchCmd = `${clientApi}api/timesheets/GetExpenseAttachments`;
            const response = await axiosInstance.get(fetchCmd, {
                params: { username: username, tsnoUI: tsnoUI },
            })
            setIsLoading(false)
            //console.log(response.data)
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const loadTimesheetInfo = async (
        tsnoUI
    ) => {
        try {
            setIsLoading(true)
            var fetchCmd = `${clientApi}api/timesheets/GetTimesheetInfo`;
            const response = await axiosInstance.get(fetchCmd, {
                params: { username: username, tsnoUI: tsnoUI },
            })
            setIsLoading(false)
            //console.log(response.data)
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const loadTimeScheme = async (
        tsnoUI
    ) => {
        try {
            setIsLoading(true)
            var fetchCmd = `${clientApi}api/timesheets/GetTimeScheme`;
            const response = await axiosInstance.get(fetchCmd, {
                params: { username: username, tsnoUI: tsnoUI },
            })
            setIsLoading(false)
            //console.log(response.data)
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const loadExpCode = async (
    ) => {
        try {
            setIsLoading(true)
            var fetchCmd = `${clientApi}api/timesheets/GetExpCode`;
            const response = await axiosInstance.get(fetchCmd, {
                params: { username: username },
            })
            setIsLoading(false)
            return response.data;
        } catch (error) {
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const loadTimesheetHelper = async (
    ) => {
        try {
            setIsLoading(true)
            var fetchCmd = `${clientApi}api/timesheets/GetTimesheetHelper`;
            const response = await axiosInstance.get(fetchCmd, {
                params: { username: username },
            })
            setIsLoading(false)
            return response.data;
        } catch (error) {
            console.log(error)
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const loadPhaseCodeForClient = async (
        jobno
    ) => {
        try {
            setIsLoading(true)
            var fetchCmd = `${clientApi}api/timesheets/GetPhaseCodeForClient`;
            const response = await axiosInstance.get(fetchCmd, {
                params: { username: username, jobno: jobno },
            })
            setIsLoading(false)
            return response.data;
        } catch (error) {
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const loadTSAEmployeeList = async (
    ) => {
        try {
            setIsLoading(true)
            var fetchCmd = `${clientApi}api/TSA/GetTSAEmployeeList`;
            const response = await axiosInstance.get(fetchCmd, {
                params: { username: username },
            })
            setIsLoading(false)
            //console.log(response.data)
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const loadEmplPendingTSA = async (
        sId,
    ) => {
        try {
            setIsLoading(true)
            var fetchCmd = `${clientApi}api/TSA/GetEmployeesPendingTS`;
            const response = await axiosInstance.get(fetchCmd, {
                params: { username: username, sId: sId },
            })
            setIsLoading(false)
            return response.data;
        } catch (error) {
            console.log(error)
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const loadEmplSubmittedTS = async ( sId) => {
        try {
            setIsLoading(true);
            const fetchCmd = `${clientApi}api/TSA/GetSubmittedTSs`;
            const response = await axiosInstance.get(fetchCmd, {
                params: { username: username, sId: sId }
            });
            setIsLoading(false);
            return response.data;
        } catch (error) {
            //console.error('Error loading submitted timesheets:', error);
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    const loadEmplApprovedTS = async ( sId) => {
        try {
            setIsLoading(true);
            const fetchCmd = `${clientApi}api/TSA/GetApprovedTSs`;
            const response = await axiosInstance.get(fetchCmd, {
                params: { username: username, sId: sId }
            });
            setIsLoading(false);
            return response.data;
        } catch (error) {
            //console.error('Error loading approved timesheets:', error);
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    const loadEmplUnsubmittedTS = async ( sId) => {
        try {
            setIsLoading(true);
            const fetchCmd = `${clientApi}api/TSA/GetUnsubmittedTSs`;
            const response = await axiosInstance.get(fetchCmd, {
                params: { username: username, sId: sId }
            });
            setIsLoading(false);
            //console.log(response)
            return response.data;
        } catch (error) {
            //console.error('Error loading unsubmitted timesheets:', error);
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    const loadEmplTSADetails = async (
        tsnoUI,
    ) => {
        try {
            setIsLoading(true)
            var fetchCmd = `${clientApi}api/TSA/GetEmployeesPendingTSDetails`;
            const response = await axiosInstance.get(fetchCmd, {
                params: { username: username, tsnoUI: tsnoUI },
            })
            setIsLoading(false)
            //console.log(response.data)
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        } finally {
            setIsLoading(false)
        }
    };
    //#endregion

    //#region Approver Page Related
    const approveTimesheet = async (
        tsid,
        comment
    ) => {
        try {
            setIsLoading(true);
            const url = `${clientApi}api/TSA/TSApproval`;
            const response = await axiosInstance.post(url, null,
                {
                    headers: { Authorization: `Bearer ${user.token}` },
                    params: { username: username, tsid: tsid, comment: comment, },
                });
            setIsLoading(false);
            return response.data;
        } catch (error) {
            //console.error(error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const rejectTimesheet = async (
        tsid,
        comment
    ) => {
        try {
            setIsLoading(true);
            const url = `${clientApi}api/TSA/TSReject`;
            const response = await axiosInstance.post(url, null,
                {
                    headers: { Authorization: `Bearer ${user.token}` },
                    params: { username: username, tsid: tsid, comment: comment, },
                });
            setIsLoading(false);
            return response.data;
        } catch (error) {
            //console.error(error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };
    //#endregion

    const copyTimesheet = async (
        obj,
    ) => {
        try {
            setIsLoading(true)
            var fetchCmd = `${clientApi}api/timesheets/CopyTimesheet`;
            const response = await axiosInstance.post(fetchCmd, null, {
                params: {
                    username: username,
                    toTS: obj.toTimesheet,
                    fromTS: obj.fromTimesheet,
                    onlyLines: obj.onlyLines,
                }
            })
            setIsLoading(false)
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const copyTSLines = async (
        toTS,
        selectedLines
    ) => {
        try {
            setIsLoading(true)
            var fetchCmd = `${clientApi}api/timesheets/CopyTimesheetLines`;
            const response = await axiosInstance.post(fetchCmd, selectedLines, {
                params: {
                    username: username,
                    toTS: toTS,
                }
            })
            setIsLoading(false)
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const deleteTimesheet = async (
        tsnoUI
    ) => {
        try {
            setIsLoading(true)
            var fetchCmd = `${clientApi}api/timesheets/DeleteTimesheet`;
            const response = await axiosInstance.post(fetchCmd, null, {
                params: {
                    username: username,
                    tsnoUI: tsnoUI,
                }
            })
            setIsLoading(false)
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    const printTimesheet = async (
        tsnoUI
    ) => {
        try {
            setIsLoading(true)
            var fetchCmd = `${clientApi}api/timesheets/PrintTimesheet`;
            const response = await axiosInstance.post(fetchCmd, null, {
                params: {
                    username: username,
                    tsnoUI: tsnoUI,
                }
            })
            setIsLoading(false)
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            setIsLoading(false)
        }
    };

    return {
        saveTimesheetLines, saveTimesheetInfo, submitTimesheet, deleteTimesheetLines,
        loadUserTimesheets, loadNextTimesheet, loadNonChargeableLines, loadChargeableLines,
        loadTimesheetInfo, loadTimesheetHelper, loadTSAEmployeeList, loadEmplPendingTSA,
        loadEmplTSADetails, loadEmplSubmittedTS, loadEmplApprovedTS, loadEmplUnsubmittedTS,
        approveTimesheet, rejectTimesheet, loadExpenseLines, loadExpCode, saveExpenseLines,
        deleteExpenseLines, saveAttachments, loadExpenseAttachments, deleteAttachments,
        loadNewTimesheet, copyTimesheet, copyTSLines, deleteTimesheet, printTimesheet,
        loadPhaseCodeForClient, loadTimeScheme, getEType, getTSDates, getPhotoByID
    };
};